import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useQuestionListQuery } from '@/entities/question';
import { useCurrentDecedent } from '@/app/providers/DecedentProvider';
import { useLocale } from '@/common/hooks/index';
import { formatDisplayDate } from '@/common/utils/index';
import decedentInfoOverlayStyles from '@/common/containers/Modals/DecedentInfoOverlay/decedent-info-overlay.styles';
import { Divider, Stack } from '@mui/material';

const STATUSES_WITH_SPOUSE = ['Married', 'Common-law', 'Separated', 'Divorced'];

const DecedentInfoOverlay = ({ open = false, onClose }) => {
  const { t } = useTranslation();
  const { isFr } = useLocale();
  const decedent = useCurrentDecedent();
  const { data: dependantsData } = useQuestionListQuery(
    'basic_dependants',
    decedent?.id,
    { enabled: open && !!decedent?.id }
  );

  const dependants = useMemo(() => {
    if (!dependantsData?.answers) {
      return null;
    }

    return dependantsData.answers.reduce(
      (acc, item) => {
        const { slug, value, status } = item;
        if (slug === 'DecMaritalStat' && status === 'active') {
          acc.marital.status = value;
        }

        if (slug === 'DecSpseContactInfo' && status === 'active') {
          acc.marital.name = `${value.firstName} ${value.middleName} ${value.lastName}`;
        }
        if (slug === 'DecDependData') {
          acc.rest.push({
            name: `${value.firstName} ${value.middleName} ${value.lastName}`,
            status: value.relationship,
          });
        }

        return acc;
      },
      { marital: {}, rest: [] }
    );
  }, [dependantsData?.answers]);

  if (!decedent) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { maxWidth: '482px', width: '100%' } }}
    >
      <Box px={4} py={5} display="flex">
        <IconButton onClick={onClose} size="small" sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="h5" fontSize="24px" ml={8} mb={6}>
        {decedent.firstName} {decedent.middleName} {decedent.lastName}
      </Typography>
      <Stack px={4} gap={8}>
        <Stack gap={4}>
          <Box sx={decedentInfoOverlayStyles.sectionHeader}>
            <Typography variant="subtitle1">{t('General info')}</Typography>
          </Box>
          <Stack sx={decedentInfoOverlayStyles.sectionBody}>
            <Box display="flex" gap={4}>
              <Typography variant="subtitle1">
                {t('Social Insurance Number: ')}
              </Typography>
              <Typography>{decedent.socialInsuranceNumber}</Typography>
            </Box>
            <Box display="flex" gap={4}>
              <Typography variant="subtitle1">
                {t('Prov / Territ Health Number: ')}
              </Typography>
              <Typography>{decedent.localHealthNumber}</Typography>
            </Box>
            <Box display="flex" gap={4}>
              <Typography variant="subtitle1">{t('Born: ')}</Typography>
              <Typography>{`${formatDisplayDate(
                decedent.dateOfBirth,
                isFr ? 'fr' : 'en'
              )}, ${decedent.cityOfBirth}, ${decedent.provinceOfBirth}, ${
                decedent.countryOfBirth
              }`}</Typography>
            </Box>
            <Box display="flex" gap={4}>
              <Typography variant="subtitle1">{t('Died: ')}</Typography>
              <Typography>{`${formatDisplayDate(
                decedent.dateOfDeath,
                isFr ? 'fr' : 'en'
              )}, ${decedent.cityOfDeath}, ${decedent.provinceOfDeath}, ${
                decedent.countryOfDeath
              }`}</Typography>
            </Box>
          </Stack>
        </Stack>
        <Stack gap={4}>
          <Box sx={decedentInfoOverlayStyles.sectionHeader}>
            <Typography variant="subtitle1">{t('Legal Residence')}</Typography>
          </Box>
          <Stack sx={decedentInfoOverlayStyles.sectionBody}>
            <Typography whiteSpace="pre-line">
              {`${decedent.residenceAddressLine1}, ${decedent.residenceAddressLine2}\n${decedent.residenceCity}, ${decedent.residenceProvince}, ${decedent.residenceCountry}, ${decedent.residencePostalCode}`}
            </Typography>
          </Stack>
        </Stack>
        {dependants?.marital && (
          <Stack gap={4}>
            <Box sx={decedentInfoOverlayStyles.sectionHeader}>
              <Typography variant="subtitle1">{t('Marital Status')}</Typography>
            </Box>
            <Stack sx={decedentInfoOverlayStyles.sectionBody}>
              <Box display="flex" gap={4}>
                <Typography variant="subtitle1">{t('Status: ')}</Typography>
                <Typography>{dependants.marital.status}</Typography>
              </Box>
              {dependants.marital.name &&
                STATUSES_WITH_SPOUSE.includes(dependants.marital.status) && (
                  <Box display="flex" gap={4}>
                    <Typography variant="subtitle1">
                      {t('Partner Name: ')}
                    </Typography>
                    <Typography>{dependants.marital.name}</Typography>
                  </Box>
                )}
            </Stack>
          </Stack>
        )}
        {dependants?.rest.length > 0 && (
          <Stack gap={4}>
            <Box sx={decedentInfoOverlayStyles.sectionHeader}>
              <Typography variant="subtitle1">{t('Dependants')}</Typography>
            </Box>
            {dependants.rest.map(({ name, status }, index) => (
              <>
                <Stack sx={decedentInfoOverlayStyles.sectionBody} key={name}>
                  <Box display="flex" gap={4}>
                    <Typography variant="subtitle1">{t('Name: ')}</Typography>
                    <Typography>{name}</Typography>
                  </Box>
                  <Box display="flex" gap={4}>
                    <Typography variant="subtitle1">
                      {t('Relationship: ')}
                    </Typography>
                    <Typography>{status}</Typography>
                  </Box>
                  {index < dependants.rest.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </Stack>
              </>
            ))}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default DecedentInfoOverlay;
